import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { Fragment, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { bool, shape, string } from 'prop-types';
import { Link } from 'react-router-dom';
import { useScrollLock, useToasts } from '@magento/peregrine';
import { useMiniCart } from '@magento/peregrine/lib/talons/MiniCart/useMiniCart';
import { useStyle } from '@magento/venia-ui/lib/classify';
import Price from '@magento/venia-ui/lib/components/Price';
import { Button } from 'src/components';
import ProductList from './ProductList';
import defaultClasses from './miniCart.module.css';
import operations from './miniCart.gql';
import { Spinner } from '@magento/venia-ui/lib/components/LoadingIndicator';
/**
 * The MiniCart component shows a limited view of the user's cart.
 *
 * @param {Boolean} props.isOpen - Whether or not the MiniCart should be displayed.
 * @param {Function} props.setIsOpen - Function to toggle mini cart
 */

var MiniCart = React.forwardRef(function (props, ref) {
  var isOpen = props.isOpen,
      setIsOpen = props.setIsOpen; // Prevent the page from scrolling in the background
  // when the MiniCart is open.

  useScrollLock(isOpen);
  var talonProps = useMiniCart({
    isOpen: isOpen,
    setIsOpen: setIsOpen,
    operations: operations
  });
  var closeMiniCart = talonProps.closeMiniCart,
      errorMessage = talonProps.errorMessage,
      handleEditCart = talonProps.handleEditCart,
      handleProceedToCheckout = talonProps.handleProceedToCheckout,
      handleRemoveItem = talonProps.handleRemoveItem,
      loading = talonProps.loading,
      productList = talonProps.productList,
      subTotal = talonProps.subTotal,
      totalQuantity = talonProps.totalQuantity,
      configurableThumbnailSource = talonProps.configurableThumbnailSource,
      storeUrlSuffix = talonProps.storeUrlSuffix;
  var classes = useStyle(defaultClasses, props.classes);
  var rootClass = isOpen ? classes.root_open : classes.root;
  var contentsClass = isOpen ? classes.contents_open : classes.contents;
  var quantityClassName = loading ? classes.quantity_loading : classes.quantity;
  var priceClassName = loading ? classes.price_loading : classes.price;
  var isCartEmpty = useMemo(function () {
    return Array.isArray(productList) && !(productList !== null && productList !== void 0 && productList.length);
  }, [productList]);

  var _useToasts = useToasts(),
      _useToasts2 = _slicedToArray(_useToasts, 2),
      addToast = _useToasts2[1].addToast;

  useEffect(function () {
    if (errorMessage) {
      addToast({
        type: 'error',
        // icon: errorIcon,
        message: errorMessage,
        dismissable: true,
        timeout: 7000
      });
    }
  }, [addToast, errorMessage]);
  var header = subTotal ? React.createElement(Fragment, null, "Cart", React.createElement("span", {
    className: quantityClassName
  }, React.createElement(FormattedMessage, {
    id: 'miniCart.totalQuantity',
    defaultMessage: '{totalQuantity} items',
    values: {
      totalQuantity: totalQuantity
    }
  }))) : null;
  var contents = isCartEmpty ? React.createElement("div", {
    className: classes.emptyCart
  }, React.createElement("div", {
    className: classes.emptyMessage
  }, React.createElement(FormattedMessage, {
    id: 'miniCart.emptyMessage',
    defaultMessage: 'There are no items in your cart.'
  }))) : React.createElement(Fragment, null, React.createElement("h5", {
    className: "text-black py-3 px-5 flex font-body justify-between items-center border-b border-solid border-gray"
  }, header), React.createElement("div", {
    className: classes.body
  }, !productList ? React.createElement(Spinner, null) : React.createElement(React.Fragment, null, React.createElement(ProductList, {
    items: productList,
    loading: loading,
    handleRemoveItem: handleRemoveItem,
    closeMiniCart: closeMiniCart,
    configurableThumbnailSource: configurableThumbnailSource,
    storeUrlSuffix: storeUrlSuffix
  }), React.createElement("div", {
    className: "flex justify-between items-center mt-7"
  }, React.createElement("h6", {
    className: "text-black font-bold"
  }, "SUBTOTAL"), React.createElement("span", {
    className: priceClassName
  }, React.createElement(Price, {
    currencyCode: subTotal.currency,
    value: subTotal.value,
    classes: {
      root: 'text-black font-bold text-h6'
    }
  }))))), React.createElement("div", {
    className: classes.footer
  }, React.createElement(Button, {
    onClick: handleProceedToCheckout,
    priority: "normal" // className={classes.checkoutButton}
    ,
    disabled: loading || isCartEmpty,
    "aria-label": "Proceed to checkout page"
  }, "PROCEED TO CHECKOUT"), React.createElement(Link, {
    className: "underline my-4 text-bronze font-bold",
    to: "/cart",
    onClick: function onClick() {
      return setIsOpen(false);
    },
    "aria-label": "Go to Cart page"
  }, "View Cart")));
  return React.createElement("aside", {
    className: rootClass
  }, React.createElement("div", {
    ref: ref,
    className: contentsClass
  }, isOpen ? contents : null));
});
export default MiniCart;
MiniCart.propTypes = {
  classes: shape({
    root: string,
    root_open: string,
    contents: string,
    contents_open: string,
    header: string,
    body: string,
    footer: string,
    checkoutButton: string,
    editCartButton: string,
    emptyCart: string,
    emptyMessage: string,
    stockStatusMessageContainer: string
  }),
  isOpen: bool
};